import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { http } from "../lib/api";
import { Clock, ArrowLeft } from "lucide-react";

// Minimal markdown-ish formatter: paragraphs separated by \n\n, **bold**
function renderParagraph(text, i) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g).map((p, j) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return <strong key={j} className="font-semibold" style={{ color: "var(--vdz-navy)" }}>{p.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={j}>{p}</React.Fragment>;
  });
  return <p key={i} className="mb-5 leading-[1.9] text-[17px]" style={{ color: "var(--vdz-text-body)" }}>{parts}</p>;
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);

  useEffect(() => {
    http.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => {});
  }, [slug]);

  if (!post) return <div className="py-32 text-center">Se încarcă...</div>;

  const fmtDate = (iso) => new Date(iso).toLocaleDateString("ro-RO", { day: "numeric", month: "long", year: "numeric" });

  return (
    <div data-testid="blog-post-page">
      {/* Hero */}
      <section className="relative py-16 md:py-24" style={{ background: "var(--vdz-navy)", color: "var(--vdz-cream)" }}>
        <div className="container-vdz max-w-3xl text-center">
          <Link to="/jurnal" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-serif-accent opacity-70 hover:opacity-100 hover:text-[color:var(--vdz-gold)] mb-8" data-testid="blog-post-back">
            <ArrowLeft size={14} /> Înapoi la jurnal
          </Link>
          <div className="eyebrow mb-5">{post.category_label || post.category}</div>
          <h1 className="font-serif-display text-4xl md:text-6xl leading-[1.1]" data-testid="blog-post-title">{post.title}</h1>
          <div className="mt-8 flex items-center justify-center gap-5 text-xs opacity-80 font-serif-accent uppercase tracking-[0.18em]">
            <span>{post.author}</span>
            <span>·</span>
            <span>{fmtDate(post.published_at)}</span>
            <span>·</span>
            <span className="flex items-center gap-1.5"><Clock size={12} /> {post.reading_time} min</span>
          </div>
        </div>
      </section>

      {/* Cover */}
      <section className="container-vdz max-w-4xl -mt-10 md:-mt-16 relative z-10">
        <div className="gold-frame overflow-hidden">
          <img src={post.cover_image} alt={post.title} className="w-full h-auto" data-testid="blog-post-cover" />
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="container-vdz max-w-2xl">
          <div className="font-serif-display text-xl md:text-2xl italic mb-10 pl-6 border-l-2" style={{ borderColor: "var(--vdz-gold)", color: "var(--vdz-navy)" }}>
            {post.excerpt}
          </div>
          <article data-testid="blog-post-content">
            {post.content.split(/\n\n+/).map((p, i) => renderParagraph(p, i))}
          </article>

          <div className="gold-divider my-16"><span className="eyebrow">Alina Stancu</span></div>

          <div className="text-center">
            <p className="opacity-80 italic font-serif-display text-xl mb-6" style={{ color: "var(--vdz-navy)" }}>
              Spor la colorat și gânduri bune. ✨
            </p>
            <Link to="/carti" className="btn-gold" data-testid="blog-post-cta">Descoperă cărțile Vis de Zi</Link>
          </div>
        </div>
      </section>

      {/* Related */}
      {post.related && post.related.length > 0 && (
        <section className="py-16 md:py-24" style={{ background: "var(--vdz-cream-warm)" }}>
          <div className="container-vdz">
            <div className="text-center mb-12">
              <div className="eyebrow mb-3">Alte articole</div>
              <h2 className="font-serif-display text-3xl md:text-4xl" style={{ color: "var(--vdz-navy)" }}>Mai mult din Jurnalul Vis de Zi</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {post.related.map((r) => (
                <Link key={r.id} to={`/jurnal/${r.slug}`} className="group block" data-testid={`blog-related-${r.slug}`}>
                  <div className="aspect-[4/5] overflow-hidden gold-frame mb-5" style={{ background: "var(--vdz-navy)" }}>
                    <img src={r.cover_image} alt={r.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <span className="px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] font-serif-accent" style={{ background: "white", color: "var(--vdz-navy)" }}>
                    {r.category_label || r.category}
                  </span>
                  <h3 className="font-serif-display text-xl mt-4 leading-tight group-hover:text-[color:var(--vdz-gold)] transition-colors" style={{ color: "var(--vdz-navy)" }}>
                    {r.title}
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
